import cn from 'classnames';
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import type { RecordingStopReason } from '@/shared/api/transcriptor';
import {
  useAppDispatch,
  useAppSelector,
  useUserTimezone,
} from '@/shared/hooks';
import { debug, trackEvent } from '@/shared/lib';
import { formatLocalizedDateTime } from '@/shared/lib/helpers/formatLocalizedDateTime';
import { formatTime } from '@/shared/lib/helpers/formatTime';
import { Button } from '@/shared/ui';

import { ChecklistPanel } from '@/entities/templates';
import {
  trackRecordingEvent,
  transcriptorModel,
  useConsultationPolling,
  useNetworkStatus,
  useRecordingBeforeUnload,
  useRecordingInterruptionDetection,
} from '@/entities/transcriptor';
import { userModel } from '@/entities/user';

import { audioRecordingService } from '@/features/audioRecording';

import {
  audioIssueMessages,
  DragState,
  RecordingToastPosition,
  VISUALIZER_BAR_COUNT,
  VISUALIZER_BAR_MIN_HEIGHT,
  VISUALIZER_FRAME_INTERVAL_MS,
} from '../../config';
import { useRecordingChecklists } from '../../hooks';
import {
  clampRecordingToastPosition,
  computeVisualizerBars,
  getInitialRecordingToastPosition,
  getToastSize,
  getViewportSize,
  saveRecordingToastPosition,
} from '../../lib';

import styles from './ClassicRecordingToast.module.scss';

const {
  pauseRecording,
  resetRecording,
  resumeRecording,
  startUploading,
  stopRecording,
} = transcriptorModel.actions;

const {
  selectConsultationId,
  selectFragmentId,
  selectIsPaused,
  selectIsUploading,
  selectIsProcessing,
  selectIsRecording,
  selectIsOffline,
  selectLastSavedAt,
  selectIsInterrupted,
  selectIsSignalLost,
  selectAudioIssue,
  selectPatientId,
  selectRecordingSchemaId,
  selectRecordingTime,
  selectUploadProgress,
} = transcriptorModel.selectors;

const { finishInterruptedRecording } = transcriptorModel.thunks;

export const ClassicRecordingToast: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const userTimezone = useUserTimezone();
  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);
  const isRecording = useAppSelector(selectIsRecording);
  const isPaused = useAppSelector(selectIsPaused);
  const recordingTime = useAppSelector(selectRecordingTime);
  const isUploading = useAppSelector(selectIsUploading);
  const isProcessing = useAppSelector(selectIsProcessing);
  const uploadProgress = useAppSelector(selectUploadProgress);
  const consultationId = useAppSelector(selectConsultationId);
  const fragmentId = useAppSelector(selectFragmentId);
  const patientId = useAppSelector(selectPatientId);
  const isOffline = useAppSelector(selectIsOffline);
  const lastSavedAt = useAppSelector(selectLastSavedAt);
  const isInterrupted = useAppSelector(selectIsInterrupted);
  const isSignalLost = useAppSelector(selectIsSignalLost);
  const audioIssue = useAppSelector(selectAudioIssue);
  const [visualizerHeights, setVisualizerHeights] = useState<number[]>(
    Array(VISUALIZER_BAR_COUNT).fill(VISUALIZER_BAR_MIN_HEIGHT),
  );
  const [position, setPosition] = useState<RecordingToastPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const toastRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<RecordingToastPosition | null>(null);
  const dragStateRef = useRef<DragState | null>(null);

  const isVisible = isRecording || isUploading || isProcessing || isInterrupted;

  const organizationId = useAppSelector(
    transcriptorModel.selectors.selectRecordingOrganizationId,
  );
  const schemaId = useAppSelector(selectRecordingSchemaId);
  const checklistTabs = useRecordingChecklists(
    isVisible ? schemaId : undefined,
    organizationId,
  );

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useNetworkStatus();
  useRecordingBeforeUnload();

  useEffect(() => {
    audioRecordingService.setOnlineStatus(!isOffline);
  }, [isOffline]);

  const getLastActivityTimestamp = useCallback(
    () => audioRecordingService.getLastDataTimestamp(),
    [],
  );
  const hasCaptureEnded = useCallback(
    () => audioRecordingService.hasTrackEnded(),
    [],
  );
  // Capture is released only for an ended track: otherwise the browser keeps
  // showing a live mic while the toast says the recording stopped.
  const handleInterrupted = useCallback(
    () => audioRecordingService.suspendForInterruption(),
    [],
  );
  const handleSignalLostChange = useCallback(
    (isLost: boolean) =>
      trackRecordingEvent(isLost ? 'signal_lost' : 'signal_restored', {
        encounter_id: consultationId,
        fragment_id: fragmentId,
      }),
    [consultationId, fragmentId],
  );
  useRecordingInterruptionDetection({
    getLastActivityTimestamp,
    hasCaptureEnded,
    onInterrupted: handleInterrupted,
    onSignalLostChange: handleSignalLostChange,
  });

  const finishRecording = useCallback(
    async (stopReason: RecordingStopReason) => {
      try {
        dispatch(startUploading());
        await audioRecordingService.stopRecording(stopReason);
        // stopRecording action is dispatched by the onStopped callback in useStartRecording
      } catch (error) {
        debug(error);
      }
    },
    [dispatch],
  );

  const formattedLastSavedTime = useMemo(() => {
    if (!lastSavedAt) {
      return null;
    }

    const { formattedTime: savedTime } = formatLocalizedDateTime({
      date: lastSavedAt,
      intl,
      timeZone: userTimezone,
    });

    return savedTime;
  }, [lastSavedAt, intl, userTimezone]);

  useEffect(() => {
    if (isRecording && !isProcessing && !audioRecordingService.isRecording()) {
      // No consultation data means a real failure, not just a post-navigation reinit
      if (!consultationId || !fragmentId) {
        dispatch(resetRecording());
      }
    }
  }, [isRecording, isProcessing, consultationId, fragmentId]);

  const handleConsultationComplete = useCallback(
    (completedConsultationId: string) => {
      dispatch(resetRecording());

      if (patientId) {
        navigate(
          `/patient/${patientId}/dental-consultation?id=${completedConsultationId}`,
        );
      }
    },
    [navigate, patientId],
  );

  useConsultationPolling({
    consultationId,
    isProcessing,
    onComplete: handleConsultationComplete,
  });

  useLayoutEffect(() => {
    if (!isVisible) {
      setPosition(null);
      dragStateRef.current = null;
      setIsDragging(false);
      setIsChecklistOpen(false);
      return;
    }

    setPosition(
      getInitialRecordingToastPosition({
        currentUserID,
        toastElement: toastRef.current,
      }),
    );
  }, [currentUserID, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleResize = () => {
      setPosition((currentPosition) => {
        if (!currentPosition) {
          return getInitialRecordingToastPosition({
            currentUserID,
            toastElement: toastRef.current,
          });
        }

        return clampRecordingToastPosition({
          position: currentPosition,
          size: getToastSize(toastRef.current),
          viewport: getViewportSize(),
        });
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [currentUserID, isVisible]);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;

      if (!dragState) {
        return;
      }

      if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {
        return;
      }

      const nextPosition = clampRecordingToastPosition({
        position: {
          x: dragState.startPosition.x + event.clientX - dragState.pointerX,
          y: dragState.startPosition.y + event.clientY - dragState.pointerY,
        },
        size: getToastSize(toastRef.current),
        viewport: getViewportSize(),
      });

      positionRef.current = nextPosition;
      setPosition(nextPosition);
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
      setIsDragging(false);

      if (positionRef.current && currentUserID) {
        saveRecordingToastPosition({
          position: positionRef.current,
          storage: window.localStorage,
          userID: currentUserID,
        });
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [currentUserID, isDragging]);

  const handleDragStart = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {
        return;
      }

      if (!positionRef.current) {
        const initialPosition = getInitialRecordingToastPosition({
          currentUserID,
          toastElement: toastRef.current,
        });
        positionRef.current = initialPosition;
        setPosition(initialPosition);
      }

      if (!positionRef.current) {
        return;
      }

      event.preventDefault();

      setIsChecklistOpen(false);

      dragStateRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        startPosition: positionRef.current,
      };
      setIsDragging(true);
    },
    [currentUserID],
  );

  useEffect(() => {
    if (!isRecording || isPaused) {
      setVisualizerHeights(
        Array(VISUALIZER_BAR_COUNT).fill(VISUALIZER_BAR_MIN_HEIGHT),
      );
      return;
    }

    let animationFrameId: number;
    let lastUpdateTime = 0;

    const updateVisualizer = (timestamp: number) => {
      if (timestamp - lastUpdateTime >= VISUALIZER_FRAME_INTERVAL_MS) {
        setVisualizerHeights(
          computeVisualizerBars(
            audioRecordingService.getAudioLevel(),
            timestamp,
          ),
        );
        lastUpdateTime = timestamp;
      }

      animationFrameId = requestAnimationFrame(updateVisualizer);
    };

    animationFrameId = requestAnimationFrame(updateVisualizer);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isRecording, isPaused]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      ref={toastRef}
      data-component="recording-toast"
      className={cn(styles.recordingToast, {
        [styles.dragging]: isDragging,
      })}
      style={
        position
          ? {
              bottom: 'auto',
              left: `${position.x}px`,
              top: `${position.y}px`,
            }
          : undefined
      }
    >
      {isChecklistOpen && checklistTabs.length > 0 && (
        <ChecklistPanel
          className={styles.checklistPanel}
          tabs={checklistTabs}
          triggerRef={toastRef}
          onClose={() => setIsChecklistOpen(false)}
        />
      )}

      <div className={styles.header}>
        <h3 className={cn('h5', styles.title)}>
          <FormattedMessage
            id="recordingToast.recording"
            defaultMessage="Recording consultation"
          />
        </h3>
        {checklistTabs.length > 0 && !isProcessing && !isInterrupted && (
          <Button
            variant="gray"
            size="small"
            className={styles.checklistButton}
            onClick={() => setIsChecklistOpen((isOpen) => !isOpen)}
          >
            <FormattedMessage
              id="recordingToast.openChecklist"
              defaultMessage="Open checklist"
            />
          </Button>
        )}
        <button
          type="button"
          className={styles.dragHandleButton}
          aria-label={intl.formatMessage({
            id: 'recordingToast.moveWidget',
            defaultMessage: 'Move recording widget',
          })}
          onPointerDown={handleDragStart}
        >
          <span className={styles.dragHandle} aria-hidden />
        </button>
      </div>

      {isOffline && isRecording && !isInterrupted && (
        <div className={styles.offlineBanner}>
          <FormattedMessage
            id="recordingToast.reconnecting"
            defaultMessage="Reconnecting... Your recording is saved and secure."
          />
        </div>
      )}

      {audioIssue &&
        isRecording &&
        !isPaused &&
        !isInterrupted &&
        !isSignalLost && (
          <div className={styles.audioIssueBanner}>
            <FormattedMessage {...audioIssueMessages[audioIssue]} />
          </div>
        )}

      {isSignalLost && isRecording && !isPaused && !isInterrupted && (
        <div className={styles.interruptedSection}>
          <div className={styles.interruptedMessage}>
            {audioIssue && audioIssue !== 'silence' ? (
              <FormattedMessage {...audioIssueMessages[audioIssue]} />
            ) : (
              <FormattedMessage
                id="recordingToast.noAudioSignal"
                defaultMessage="No audio for over a minute. Check your microphone or generate the transcript from what was recorded."
              />
            )}
          </div>
          <div className={styles.controls}>
            <Button
              variant="primary"
              size="small"
              onClick={() => finishRecording('client_interrupted')}
              className={styles.controlButton}
              disabled={isUploading}
            >
              <FormattedMessage
                id="recordingToast.generateTranscript"
                defaultMessage="Generate Transcript"
              />
            </Button>
          </div>
        </div>
      )}

      {isInterrupted && !isProcessing && (
        <div className={styles.interruptedSection}>
          <div className={styles.interruptedMessage}>
            <FormattedMessage
              id="recordingToast.recordingStopped"
              defaultMessage="Recording stopped - last saved at {time}. Device may have disconnected."
              values={{ time: formattedLastSavedTime || '--:--' }}
            />
          </div>
          <div className={styles.controls}>
            <Button
              variant="primary"
              size="small"
              onClick={async () => {
                try {
                  if (consultationId && fragmentId) {
                    dispatch(stopRecording());
                    if (!audioRecordingService.finishInterruptedRecording()) {
                      await dispatch(
                        finishInterruptedRecording({
                          consultationId,
                          fragmentId,
                          reason: 'client_interrupted',
                        }),
                      ).unwrap();
                    }
                  }
                } catch (error) {
                  debug(error);
                }
              }}
              className={styles.controlButton}
            >
              <FormattedMessage
                id="recordingToast.generateTranscript"
                defaultMessage="Generate Transcript"
              />
            </Button>
          </div>
        </div>
      )}

      {!isProcessing && !isInterrupted && (
        <div className={styles.recordingStatus}>
          <div
            className={cn(styles.recordingIndicator, {
              [styles.recording]: isRecording && !isPaused,
            })}
          />
          <span className={styles.time}>{formatTime(recordingTime)}</span>
          {isPaused && (
            <span className={styles.pausedText}>
              <FormattedMessage
                id="recordingToast.paused"
                defaultMessage="Paused"
              />
            </span>
          )}
          <div className={styles.visualizer}>
            {visualizerHeights.map((height, index) => (
              <div
                key={index}
                className={cn(styles.visualizerBar, {
                  [styles.paused]: isPaused,
                })}
                style={
                  {
                    '--bar-height': `${height}px`,
                  } as React.CSSProperties
                }
              />
            ))}
          </div>

          <div className={styles.controls}>
            {isRecording && !isPaused && (
              <Button
                variant="secondary"
                size="medium"
                icon="pause"
                aria-label={intl.formatMessage({
                  id: 'recordingToast.pause',
                  defaultMessage: 'Pause',
                })}
                onClick={() => {
                  trackEvent({
                    eventName:
                      'PatientProfilePageTranscriptorRecordModalPauseButtonClicked',
                  });

                  audioRecordingService.pauseRecording();
                  dispatch(pauseRecording());
                }}
                className={styles.controlButton}
                disabled={isUploading}
              />
            )}
            {isRecording && isPaused && (
              <Button
                variant="primary"
                size="medium"
                icon="play"
                aria-label={intl.formatMessage({
                  id: 'recordingToast.resume',
                  defaultMessage: 'Resume',
                })}
                onClick={() => {
                  trackEvent({
                    eventName:
                      'PatientProfilePageTranscriptorRecordModalResumeButtonClicked',
                  });

                  audioRecordingService.resumeRecording();
                  dispatch(resumeRecording());
                }}
                className={styles.controlButton}
                disabled={isUploading}
              />
            )}
            <Button
              variant="primary"
              size="medium"
              danger
              icon="stop"
              loading={isUploading}
              aria-label={intl.formatMessage({
                id: 'recordingToast.stop',
                defaultMessage: 'Finish',
              })}
              onClick={() => {
                trackEvent({
                  eventName:
                    'PatientProfilePageTranscriptorRecordModalFinishButtonClicked',
                });

                finishRecording('user');
              }}
              className={styles.controlButton}
              disabled={isUploading}
            />
          </div>
        </div>
      )}

      {isProcessing && (
        <div className={styles.progressSection}>
          <p className={styles.progressTitle}>
            {uploadProgress > 0 ? (
              <FormattedMessage
                id="recordingToast.saving"
                defaultMessage="Saving consultation..."
              />
            ) : (
              <FormattedMessage
                id="recordingToast.processing"
                defaultMessage="Processing recording..."
              />
            )}
          </p>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={
                {
                  '--progress-width': `${uploadProgress}%`,
                } as React.CSSProperties
              }
            />
          </div>
          <p className={styles.progressText}>
            <FormattedMessage
              id="recordingToast.uploadProgress"
              defaultMessage="{progress}% complete"
              values={{ progress: uploadProgress }}
            />
          </p>
        </div>
      )}
    </div>
  );
};
